// google-auth.js - Handles Google authentication

import store from './store.js';
import { apiUrl } from './api-settings.js';
import { showNotification } from './utils.js';
import { onSuccessfulAuth } from '../main.js';
import { hideAllModals } from '../main.js';

// Variable pour suivre si setupGoogleAuth a déjà été appelée
let googleAuthInitialized = false;

/**
 * Initialize Google Sign In for desktop devices
 */
function initGoogleSignIn() {
  console.log("Initializing Google Sign In...");
  console.log("API URL used for verification:", `${apiUrl}/verify-google-token`);
  // ID client Google OAuth obtenu depuis Google Cloud Console
  const clientId = "928490682955-kfubeuenv9vtqeggh1ta997lqgqm4sq3.apps.googleusercontent.com";
  
  // URL de redirection vers cette même page frontend avec un paramètre pour indiquer que c'est un callback
  const frontendRedirectUrl = window.location.href.split('?')[0].split('#')[0]; // URL nettoyée sans paramètres ni fragments
  console.log("Frontend redirect URL:", frontendRedirectUrl);
  
  if (typeof google !== 'undefined' && google.accounts) {
    console.log("Initializing Google Sign In in REDIRECT mode (to frontend)...");
    google.accounts.id.initialize({
      client_id: clientId,
      login_uri: frontendRedirectUrl, // Redirection vers la page frontend
      ux_mode: 'redirect',
      auto_select: false,
      cancel_on_tap_outside: true,
      // Ajouter callback comme fallback - on essaie la redirection d'abord, mais si ça échoue, on utilise le callback
      callback: function(response) {
        console.log("Google callback fallback triggered! Response:", response);
        if (response.credential) {
          // Simuler une redirection en traitant directement le token
          console.log("Processing credential via callback fallback");
          // On peut appeler directement notre fonction de vérification
          handleCredential(response.credential);
        }
      }
    });
    console.log("Google Sign-In initialized for redirect flow with client ID:", clientId);
    
    // Le prompt ou le bouton déclenchera la redirection vers frontendRedirectUrl
    console.log("Calling google.accounts.id.prompt() for redirect flow...");
    google.accounts.id.prompt((notification) => {
      console.log("Google prompt notification:", notification);
    });
  } else {
    console.log("Google accounts API not found, trying to load it...");
    // If the Google library isn't loaded yet
    showNotification("Google Sign-In API is loading. Please try again in a moment.", "warning");
    
    // Reload the script
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      console.log("Google SDK loaded successfully");
      initGoogleSignIn();
    };
    document.head.appendChild(script);
    console.log("Google SDK script added to the document head");
  }
}

/**
 * Handle Google Sign In callback
 * @param {Object} response - The response from Google Sign In
 */
/* // NOTE: Cette fonction n'est plus appelée directement par Google en mode redirect.
   // La logique de vérification du token doit être implémentée dans le backend
   // à l'URL spécifiée dans login_uri (`${apiUrl}/auth/google/callback`).
   // Le backend, après vérification, doit rediriger l'utilisateur vers le frontend,
   // potentiellement avec un token de session (cookie) ou un token JWT transmis autrement.
async function handleGoogleSignIn(response) {
  console.log("handleGoogleSignIn function entered."); // Ne sera pas appelé par GSI
  try {
    console.log("Handling Google sign-in response", response);
    console.log("Google sign-in callback triggered with credential:", response.credential ? "Present" : "Missing");

    if (!response.credential) {
      throw new Error("No credential returned from Google");
    }

    const idToken = response.credential;
    console.log("ID token received from Google, first 10 chars:", idToken.substring(0, 10) + "...");

    // Send the token to the server for verification
    console.log("Sending token to server for verification at:", `${apiUrl}/verify-google-token`);
    const verifyResponse = await fetch(`${apiUrl}/verify-google-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ idToken })
    });

    console.log("Server verification response status:", verifyResponse.status);

    if (!verifyResponse.ok) {
      const errorData = await verifyResponse.json();
      console.error("Token verification failed:", errorData);
      throw new Error(errorData.error || "Failed to verify Google token");
    }

    const data = await verifyResponse.json();
    console.log("Token verification successful, received data:", data);

    // Store JWT in localStorage
    localStorage.setItem('quantumAuthToken', data.token);
    console.log("Auth token stored in localStorage");

    // Update authentication state
    store.setAuthenticated(true);
    store.setGuestMode(false);
    store.setWalletAddress(null);
    console.log("Authentication state updated in store");

    // Display user interface
    hideAllModals();
    onSuccessfulAuth();
    console.log("Authentication successful, UI updated");

    showNotification("Successfully signed in with Google!", "success");
  } catch (error) {
    console.error("Google authentication error:", error);
    showNotification(`Error: ${error.message}`, "error");
  }
}
*/

// Exposer la fonction au contexte global pour le script inline
// window.handleGoogleAuth = handleGoogleSignIn; // N'est plus nécessaire pour GSI callback

/**
 * Check if current device is mobile
 * @returns {boolean} - True if the device is mobile
 */
function isMobileDevice() {
  return (
    window.innerWidth <= 768 ||
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    )
  );
}

/**
 * Initialize Google Sign In for mobile devices
 * @returns {HTMLElement} - The Google Sign In button for mobile
 */
function initGoogleSignInForMobile() {
  const clientId = "928490682955-kfubeuenv9vtqeggh1ta997lqgqm4sq3.apps.googleusercontent.com"; // ID client fixe
  console.log("Initializing Google Sign-In for mobile (dynamic button creation). Client ID:", clientId);
  
  const googleBtnContainer = document.getElementById("googleBtnContainer");
  if (!googleBtnContainer) {
    console.error("Google button container not found!");
    return null; // Return null or handle error appropriately
  }
  
  // Clear any existing button in the container first
  googleBtnContainer.innerHTML = '';
  console.log("Cleared existing content in googleBtnContainer");

  // Create a custom button for mobile
  const mobileGoogleBtn = document.createElement('button');
  mobileGoogleBtn.className = 'connect-btn';
  mobileGoogleBtn.style.backgroundColor = 'white';
  mobileGoogleBtn.style.color = '#444';
  mobileGoogleBtn.style.border = '1px solid #ddd';
  mobileGoogleBtn.style.display = 'flex';
  mobileGoogleBtn.style.alignItems = 'center';
  mobileGoogleBtn.style.justifyContent = 'center';
  mobileGoogleBtn.style.padding = '10px 20px';
  mobileGoogleBtn.style.borderRadius = '4px';
  mobileGoogleBtn.style.cursor = 'pointer';
  mobileGoogleBtn.style.width = '100%';
  mobileGoogleBtn.style.fontWeight = 'normal';
  mobileGoogleBtn.style.margin = '10px 0';
  
  // Google logo
  mobileGoogleBtn.innerHTML = `
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" style="margin-right: 10px;">
      <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"></path>
      <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"></path>
      <path fill="#FBBC05" d="M3.964 10.712c-.18-.54-.282-1.117-.282-1.71s.102-1.17.282-1.71V4.96H.957A8.996 8.996 0 0 0 0 9.002c0 1.452.348 2.827.957 4.042l3.007-2.332z"></path>
      <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"></path>
    </svg>
    <span>Sign in with Google</span>
  `;
  
  // Click event to open browser for OAuth authentication
  mobileGoogleBtn.addEventListener('click', async function() {
    try {
      console.log("Mobile Google Sign-In button clicked - STARTING AUTHENTICATION FLOW");
      // Instead of opening a popup to a non-existent endpoint, use the Google OAuth directly
      
      // First check if Google SDK is loaded
      if (typeof google === 'undefined' || !google.accounts) {
        console.log("Google SDK not loaded, loading it now...");
        // Load the Google SDK if not already loaded
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://accounts.google.com/gsi/client';
          script.async = true;
          script.defer = true;
          script.onload = () => {
            console.log("Google SDK loaded successfully on button click");
            resolve();
          };
          script.onerror = () => {
            reject(new Error("Failed to load Google SDK"));
          };
          document.head.appendChild(script);
        });
      }
      
      console.log("Initializing Google Sign In for mobile...");
      
      // Initialize Google Sign-In with clientId
      google.accounts.id.initialize({
        client_id: clientId,
        callback: async (response) => {
          console.log("Google callback triggered on mobile");
          if (response.credential) {
            console.log("Credential received from Google, processing...");
            // Process the credential directly
            await handleCredential(response.credential);
          } else {
            console.log("No credential received from Google");
            showNotification("Authentication failed: No credential received", "error");
          }
        },
        auto_select: false,
        cancel_on_tap_outside: true
      });
      
      // Prompt the user to sign in
      console.log("Calling google.accounts.id.prompt() for mobile...");
      google.accounts.id.prompt((notification) => {
        console.log("Google prompt notification on mobile:", notification);
        
        // Check if the prompt was displayed
        if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
          const reason = notification.getNotDisplayedReason() || 
                       notification.getSkippedReason() || 
                       "unknown";
          console.log("Google prompt not displayed or skipped. Reason:", reason);
          
          // Show error to user
          showNotification(`Google Sign-In prompt couldn't be displayed: ${reason}`, "warning");
        }
      });
    } catch (error) {
      console.error("Mobile Google auth error:", error);
      showNotification(`Error: ${error.message}`, "error");
    }
  });
  
  // Append the newly created button to the container
  googleBtnContainer.appendChild(mobileGoogleBtn);
  console.log("Appended dynamically created Google mobile button to container");

  return mobileGoogleBtn;
}

/**
 * Display Google user info in UI
 * @param {Object} userData - User data from Google
 */
function displayGoogleUserInfo(userData) {
  // Find UI elements to update
  const userAvatar = document.querySelector('.user-avatar');
  
  if (userAvatar && userData.picture) {
    // Update user avatar with Google image
    userAvatar.innerHTML = `<img src="${userData.picture}" alt="${userData.name}" style="width: 100%; height: 100%; border-radius: 50%; object-fit: cover;">`;
    userAvatar.style.padding = '0';
    userAvatar.style.overflow = 'hidden';
    userAvatar.title = userData.name || userData.email;
  }
  
  // If we have a user menu, add info
  const userInfoElement = document.getElementById('userInfo');
  if (userInfoElement && userData.name) {
    userInfoElement.textContent = userData.name;
  }
  
  // Add Google badge visible in sidebar
  const headerActions = document.querySelector('.header-actions');
  if (headerActions) {
    const googleBadge = document.createElement('div');
    googleBadge.className = 'google-badge';
    googleBadge.innerHTML = `
      <img src="${userData.picture}" alt="${userData.name}" 
        style="width: 24px; height: 24px; border-radius: 50%; margin-right: 8px; border: 1px solid var(--border);">
      <span style="font-size: 12px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 120px;">${userData.name}</span>
    `;
    googleBadge.style.display = 'flex';
    googleBadge.style.alignItems = 'center';
    googleBadge.style.padding = '5px 10px';
    googleBadge.style.backgroundColor = 'var(--bg-secondary)';
    googleBadge.style.borderRadius = '4px';
    googleBadge.style.marginBottom = '10px';
    
    headerActions.parentNode.insertBefore(googleBadge, headerActions);
  }
}

/**
 * Setup Google authentication
 */
function setupGoogleAuth() {
  // Vérifier si déjà initialisé pour éviter les doublons
  if (googleAuthInitialized) {
    console.log("Google Auth already initialized, skipping setup");
    return;
  }
  
  console.log("Setting up Google Auth...");
  googleAuthInitialized = true;
  
  // Charger le SDK Google de manière proactive
  console.log("Proactively loading Google SDK");
  loadGoogleSDK();
  
  // Add callback debugging
  setupCallbackDebugging();
  
  // Si nous sommes sur mobile, initialiser directement le bouton mobile
  if (isMobileDevice()) {
    console.log("Setting up Google Sign In for mobile");
    // Call initGoogleSignInForMobile which now handles container finding and button creation/replacement
    const mobileGoogleBtn = initGoogleSignInForMobile(); 
    // No need to append here, initGoogleSignInForMobile does it if container found
    // We also removed the check for existing button here, as init handles it.
    return; // On s'arrête là pour mobile
  }
  
  // Directly attach event to the button regardless of when this function is called
  document.addEventListener('DOMContentLoaded', () => {
    console.log("DOM fully loaded, attaching Google button event");
    const googleBtn = document.getElementById('googleSignInBtn');
    if (googleBtn) {
      console.log("Google button found, attaching click event");
      // Use direct click event with inline function to avoid reference issues
      googleBtn.onclick = function() {
        console.log("Google sign-in button clicked");
        initGoogleSignIn();
      };
    } else {
      console.log("Google button not found in DOM");
    }
  });
  
  // Also try to set up immediately in case DOMContentLoaded already fired
  const googleBtn = document.getElementById('googleSignInBtn');
  if (googleBtn) {
    console.log("Google button found immediately, attaching click event");
    googleBtn.onclick = function() {
      console.log("Google sign-in button clicked");
      initGoogleSignIn();
    };
  }
}

/**
 * Set up debugging for Google Auth callback and check for redirect
 */
function setupCallbackDebugging() {
  console.log("Setting up Google Auth callback debugging...");
  
  // Check for Google redirect with token
  checkGoogleSignInRedirect();
  
  // Check if we're on a callback page
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.has('code') || urlParams.has('token') || urlParams.has('id_token')) {
    console.log("========= GOOGLE AUTH CALLBACK DETECTED =========");
    console.log("Current URL:", window.location.href);
    console.log("URL Parameters:", {
      code: urlParams.get('code'),
      token: urlParams.get('token'),
      id_token: urlParams.get('id_token'),
      state: urlParams.get('state'),
      error: urlParams.get('error')
    });
    
    // Check if we're in a popup window
    if (window.opener) {
      console.log("This window is a popup with an opener window");
      
      // If we have an id_token, try to send it back to the parent
      const idToken = urlParams.get('id_token');
      if (idToken) {
        console.log("Found ID token in URL, attempting to send to parent window");
        try {
          window.opener.postMessage({
            type: 'google-auth-success',
            idToken: idToken
          }, window.location.origin);
          console.log("Message posted to parent window");
        } catch (error) {
          console.error("Error posting message to parent:", error);
        }
      }
    } else {
      console.log("This window appears to be a main window, not a popup");
    }
  }
  
  // Listen for authentication related messages
  window.addEventListener('message', function(event) {
    // Basic check for origin and data structure before logging
    // Adjust the origin check if your callback page might have a different origin
    if (event.origin === window.location.origin && event.data && (event.data.type === 'google-auth-success' || event.data.idToken)) {
      console.log("Relevant authentication message received:", event);
      console.log("Message origin:", event.origin);
      console.log("Message data:", event.data);
    } else if (event.data && event.data.target === 'metamask-inpage') {
      // Optionally, log Metamask messages differently or ignore them
      // console.log("Metamask message detected:", event.data);
    } else {
      // Log other messages if needed for debugging, but maybe less verbosely
      // console.log("Generic message received:", event);
    }
  });
}

/**
 * Load Google SDK if not already loaded
 */
function loadGoogleSDK() {
  if (document.getElementById('google-sdk')) return;
  
  const script = document.createElement('script');
  script.id = 'google-sdk';
  script.src = 'https://accounts.google.com/gsi/client';
  script.async = true;
  script.defer = true;
  document.head.appendChild(script);
}

/**
 * Check for Google Sign-In token in the URL after redirect
 * This must be called when the page loads
 */
async function checkGoogleSignInRedirect() {
  try {
    console.log("=== CHECKING GOOGLE SIGN-IN REDIRECT ===");
    console.log("Current URL:", window.location.href);
    // Log all URL search parameters
    const urlParams = new URLSearchParams(window.location.search);
    console.log("All URL parameters:", Object.fromEntries(urlParams.entries()));

    // Log specific auth parameters
    const credential = urlParams.get('credential');
    const g_csrf_token = urlParams.get('g_csrf_token');
    const code = urlParams.get('code');
    const id_token = urlParams.get('id_token');
    const token = urlParams.get('token');
    const error = urlParams.get('error');
    
    console.log("Auth parameter check:", {
      credential: credential ? "Present" : "Missing",
      g_csrf_token: g_csrf_token ? "Present" : "Missing",
      code: code ? "Present" : "Missing",
      id_token: id_token ? "Present" : "Missing",
      token: token ? "Present" : "Missing",
      error: error || "None"
    });
    
    // Check URL hash as well (some OAuth flows use hash fragments)
    if (window.location.hash) {
      console.log("URL contains hash fragment:", window.location.hash);
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      console.log("Hash parameters:", Object.fromEntries(hashParams.entries()));
    }
    
    // Process the credential if it exists
    if (credential) {
      console.log("Google credential found in URL after redirect");
      return await handleCredential(credential);
    } else if (id_token) {
      // Certains flux OAuth renvoient id_token au lieu de credential
      console.log("Google id_token found in URL after redirect");
      return await handleCredential(id_token);
    } else if (token) {
      // Autre variante possible
      console.log("Token found in URL after redirect");
      return await handleCredential(token);
    } else if (code) {
      // Si nous avons un code, nous devons l'échanger contre un token via le backend
      console.log("Authorization code found - this requires backend exchange");
      showNotification("Authorization code flow not implemented in this frontend", "warning");
    }
  } catch (error) {
    console.error("Google redirect token handling error:", error);
    showNotification(`Error: ${error.message}`, "error");
  }
  return false;
}

/**
 * Process Google credentials from either redirect or callback
 * @param {string} credential - Google ID token
 */
async function handleCredential(credential) {
  try {
    console.log("Processing Google credential, first 10 chars:", credential.substring(0, 10) + "...");
    
    // Send the token to the server for verification
    console.log("Sending token to server for verification at:", `${apiUrl}/verify-google-token`);
    const verifyResponse = await fetch(`${apiUrl}/verify-google-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ idToken: credential })
    });
    
    console.log("Server verification response status:", verifyResponse.status);
    
    if (!verifyResponse.ok) {
      // Check content type to avoid trying to parse HTML as JSON
      const contentType = verifyResponse.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const errorData = await verifyResponse.json();
        console.error("Token verification failed:", errorData);
        throw new Error(errorData.error || `Failed to verify Google token: ${verifyResponse.status}`);
      } else {
        // Handle non-JSON error response
        const errorText = await verifyResponse.text();
        console.error("Token verification failed with non-JSON response:", {
          status: verifyResponse.status,
          contentType,
          responsePreview: errorText.substring(0, 100) + '...'
        });
        throw new Error(`Failed to verify Google token: ${verifyResponse.status} - Endpoint not found or not available`);
      }
    }
    
    const data = await verifyResponse.json();
    console.log("Token verification successful, received data:", data);
    
    // Store JWT in localStorage
    localStorage.setItem('quantumAuthToken', data.token);
    console.log("Auth token stored in localStorage");
    
    // Store user data received from the server
    if (data.user) {
      localStorage.setItem('quantumUserData', JSON.stringify({
        walletAddress: data.user.walletAddress,
        accountType: 'google', // Identifying account type
        credits: data.user.credits || 0,
        email: data.user.walletAddress, // For Google auth, email is stored as walletAddress
        lastActive: new Date().toISOString()
      }));
      console.log("User data stored in localStorage");
    }
    
    // Update authentication state
    store.setAuthenticated(true);
    store.setGuestMode(false);
    store.setWalletAddress(data.user?.walletAddress || null);
    store.setAccountType('google');
    store.setUserEmail(data.user?.walletAddress || null);
    store.setUserCredits(data.user?.credits || 0);
    console.log("Authentication state updated in store");
    
    // Display user interface
    hideAllModals();
    onSuccessfulAuth();
    console.log("Authentication successful, UI updated");
    
    showNotification("Successfully signed in with Google!", "success");
    
    // If we're on a URL with credentials, clean it up
    if (window.location.search && new URLSearchParams(window.location.search).has('credential')) {
      const cleanUrl = window.location.href.split('?')[0];
      window.history.replaceState({}, document.title, cleanUrl);
    }
    
    return true;
  } catch (error) {
    console.error("Google credential handling error:", error);
    showNotification(`Error: ${error.message}`, "error");
    return false;
  }
}

export {
  setupGoogleAuth,
  loadGoogleSDK,
  displayGoogleUserInfo,
  // handleGoogleSignIn, // Commenté puisque nous ne l'utilisons plus directement
  initGoogleSignIn,
  checkGoogleSignInRedirect, // Ajouté pour permettre de l'appeler depuis d'autres fichiers si nécessaire
  handleCredential // Ajouté pour permettre le traitement du credential directement
}; 